import { useMemo, useState, useEffect } from "react";
import { Chessboard } from "react-chessboard";
import { Chess } from "chess.js";
import { useTranslation } from "react-i18next";
import { makeTr } from "@/i18n/tr";

const PROMO = [
  { p: "q", ru: "Ферзь", en: "Queen", glyph: { w: "♕", b: "♛" } },
  { p: "r", ru: "Ладья", en: "Rook", glyph: { w: "♖", b: "♜" } },
  { p: "b", ru: "Слон", en: "Bishop", glyph: { w: "♗", b: "♝" } },
  { p: "n", ru: "Конь", en: "Knight", glyph: { w: "♘", b: "♞" } },
];

const THEMES = {
  classic: { light: "#f0d9b5", dark: "#b58863" },
  wood: { light: "#e8cda0", dark: "#a67c52" },
  blue: { light: "#dee3e6", dark: "#8ca2ad" },
  dark: { light: "#4a4a4a", dark: "#2d2d2d" },
  purple: { light: "#e8e0f0", dark: "#957bb8" },
  tournament: { light: "#eeeed2", dark: "#769656" },
  ice: { light: "#e0f4ff", dark: "#6bb3d9" },
};

export const getBoardColors = (theme) => THEMES[theme] || THEMES.tournament;

const findKing = (chess, color) => {
  for (const row of chess.board()) {
    for (const sq of row) {
      if (sq && sq.type === "k" && sq.color === color) return sq.square;
    }
  }
  return null;
};

export const GameBoard = ({
  id = "board",
  fen,
  orientation = "white",
  myColor = "white",
  canMove = false,
  onMove,
  lastMove,
  theme = "tournament",
  animationMs = 300,
  testIdPrefix = "square",
  maxSize = 640,
}) => {
  const [selected, setSelected] = useState(null);
  const [promotion, setPromotion] = useState(null);
  const { i18n } = useTranslation();
  const tr = makeTr(i18n.language);
  const chess = useMemo(() => {
    try {
      return new Chess(fen);
    } catch {
      return new Chess();
    }
  }, [fen]);

  useEffect(() => {
    setSelected(null);
    setPromotion(null);
  }, [fen]);

  const colors = getBoardColors(theme);
  const myPrefix = myColor === "white" ? "w" : "b";

  const tryMove = (from, to) => {
    if (!canMove || !from || !to || from === to) return false;
    const legal = chess.moves({ square: from, verbose: true }).find((m) => m.to === to);
    if (!legal) return false;
    if (legal.promotion) {
      setPromotion({ from, to, legal });
      setSelected(null);
      return true;
    }
    const ok = onMove ? onMove(from + to, legal) : true;
    return ok !== false;
  };

  const finishPromotion = (piece) => {
    if (!promotion) return;
    const { from, to, legal } = promotion;
    setPromotion(null);
    if (onMove) onMove(from + to + piece, { ...legal, promotion: piece });
  };

  const handleSquareClick = ({ square, piece }) => {
    if (!canMove) return;
    const pieceType = piece?.pieceType || "";
    if (selected && selected !== square) {
      if (tryMove(selected, square)) {
        setSelected(null);
        return;
      }
    }
    if (pieceType.startsWith(myPrefix)) {
      setSelected(selected === square ? null : square);
    } else {
      setSelected(null);
    }
  };

  const squareStyles = useMemo(() => {
    const styles = {};
    if (lastMove?.from) styles[lastMove.from] = { backgroundColor: "rgba(212, 175, 55, 0.45)" };
    if (lastMove?.to) styles[lastMove.to] = { backgroundColor: "rgba(212, 175, 55, 0.45)" };
    if (chess.isCheck()) {
      const king = findKing(chess, chess.turn());
      if (king) styles[king] = { background: "radial-gradient(circle, rgba(255,0,0,0.75) 0%, rgba(255,0,0,0.35) 55%, transparent 75%)" };
    }
    if (selected) {
      styles[selected] = { backgroundColor: "rgba(255, 215, 0, 0.55)" };
      for (const m of chess.moves({ square: selected, verbose: true })) {
        const capture = Boolean(chess.get(m.to)) || m.flags.includes("e");
        styles[m.to] = {
          ...(styles[m.to] || {}),
          background: capture
            ? `radial-gradient(circle, transparent 58%, rgba(0,0,0,0.28) 60%)${styles[m.to]?.backgroundColor ? `, ${styles[m.to].backgroundColor}` : ""}`
            : `radial-gradient(circle, rgba(0,0,0,0.28) 22%, transparent 24%)${styles[m.to]?.backgroundColor ? `, ${styles[m.to].backgroundColor}` : ""}`,
        };
      }
    }
    return styles;
  }, [chess, selected, lastMove]);

  return (
    <div
      className="w-full mx-auto select-none relative"
      style={{ maxWidth: `min(100vw - 2rem, calc(100vh - 270px), ${maxSize}px)` }}
      data-testid={`${testIdPrefix}-board`}
    >
      <Chessboard
        options={{
          id,
          position: fen,
          boardOrientation: orientation,
          allowDragging: canMove,
          animationDurationInMs: animationMs,
          canDragPiece: ({ piece }) => canMove && (piece?.pieceType || "").startsWith(myPrefix),
          onPieceDrop: ({ sourceSquare, targetSquare }) => tryMove(sourceSquare, targetSquare),
          onSquareClick: handleSquareClick,
          squareStyles,
          boardStyle: { borderRadius: "8px", boxShadow: "0 12px 40px rgba(0,0,0,0.45)" },
          darkSquareStyle: { backgroundColor: colors.dark },
          lightSquareStyle: { backgroundColor: colors.light },
          dropSquareStyle: { boxShadow: "inset 0 0 0 3px rgba(255,215,0,0.7)" },
        }}
      />
      {promotion && (
        <div
          className="absolute inset-0 z-20 flex items-center justify-center bg-background/70 backdrop-blur-[2px] rounded-lg"
          data-testid="promotion-dialog"
          onClick={() => setPromotion(null)}
        >
          <div className="bg-card border border-border rounded-xl p-3 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <p className="text-xs text-muted-foreground text-center mb-2">{tr("Превратить в", "Promote to")}</p>
            <div className="flex gap-2">
              {PROMO.map((o) => (
                <button
                  key={o.p}
                  type="button"
                  onClick={() => finishPromotion(o.p)}
                  data-testid={`promote-${o.p}`}
                  className="flex flex-col items-center w-16 py-2 rounded-lg border border-border hover:border-primary hover:bg-primary/10 transition-colors"
                >
                  <span className={`text-4xl leading-none ${myPrefix === "w" ? "text-white drop-shadow-[0_2px_2px_rgba(0,0,0,0.8)]" : "text-zinc-900 drop-shadow-[0_1px_1px_rgba(255,255,255,0.4)]"}`}>
                    {o.glyph[myPrefix]}
                  </span>
                  <span className="text-[11px] mt-1">{tr(o.ru, o.en)}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GameBoard;
